import { useNavigate, useParams } from "react-router";
import { useMemo, useCallback } from "react";
import {
  useFeedItemsByCareRecipientIdQuery,
  useVisitByIdQuery,
} from "../../api/generated/graphql";
import { Headline } from "@frontend/lyng/typography";
import { ActivitiesFeed } from "@frontend/lyng/activityFeed";
import { DateTime } from "luxon";
import { useCareContext } from "../../providers";
import Leaves from "../../assets/leaves.svg?react";
import { Spinner } from "../../components/common";
import { match, P } from "ts-pattern";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { useTranslate } from "@tolgee/react";

export const PastHomeVisits = () => {
  // We use carerecipientId to search for the carerecipient in the homeStays of the visit
  const { id, homeId, careRecipientId } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useVisitByIdQuery({
    variables: {
      visitInstanceId: id ?? "",
    },
  });
  const { t } = useTranslate();

  const careRecipient = useMemo(
    () =>
      data?.visitById?.homeStays?.find(
        (homeStay) => homeStay.careRecipient.id === careRecipientId,
      )?.careRecipient,
    [data?.visitById?.homeStays, careRecipientId],
  );

  const subject = useMemo(
    () => data?.visitById?.subject,
    [data?.visitById?.subject],
  );

  const { viewer } = useCareContext().state;
  const {
    data: feedData,
    error: feedError,
    loading: feedLoading,
  } = useFeedItemsByCareRecipientIdQuery({
    fetchPolicy: "network-only",
    variables: {
      from: DateTime.local().endOf("day").toISO() ?? "",
      limit: 60,
      careRecipientId: homeId ?? "",
    },
    skip: !homeId,
  });
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  const handleCardClick = useCallback(
    (id: string) => {
      navigate(`/visit/${id}`);
    },
    [navigate],
  );

  const cleanLogs = useCallback(() => {
    return feedData?.feedItemsByCareRecipientId.filter(
      (item) => item.__typename === "Visit",
    );
  }, [feedData?.feedItemsByCareRecipientId]);

  const headlineText = useMemo(
    () =>
      match({ careRecipient, homeId, subject })
        .with({ careRecipient: P.nullish, homeId: P.nullish }, () =>
          t("allVisits"),
        ) // This should never happen
        .with(
          {
            careRecipient: P.nullish,
            homeId: P.nonNullable,
            subject: P.nullish,
          },
          () => t("allVisits"),
        )
        .with(
          {
            careRecipient: P.nullish,
            homeId: P.nonNullable,
            subject: { __typename: "CareHome" },
          },
          ({ subject }) => subject.name,
        )
        .with(
          {
            careRecipient: P.nonNullable,
            homeId: P.nullish,
            subject: { __typename: "CareRecipient" },
          },
          ({ subject }) => nameOrderFn(subject),
        )
        .with(
          {
            careRecipient: P.nonNullable,
            homeId: P.nonNullable,
            subject: { __typename: "CareHome" },
          },
          ({ careRecipient, subject }) =>
            // TODO: Put in the actual string
            `${nameOrderFn(careRecipient)} @ ${subject.name}`,
        )
        .otherwise(() => t("allVisits")),
    [careRecipient, homeId, subject, nameOrderFn, t],
  );

  const filteredFeedData = useMemo(
    () =>
      match({ feedData, careRecipient })
        .with(
          { feedData: P.array(), careRecipient: P.nullish },
          () => cleanLogs() ?? [],
        )
        .with(
          { feedData: P.array(), careRecipient: P.nonNullable },
          (data) =>
            data.feedData?.feedItemsByCareRecipientId
              .filter((a) => a.__typename === "Visit")
              .filter((a) => a.subjectId === data?.careRecipient?.id) ?? [],
        )
        .otherwise(() => []),
    [feedData, careRecipient, cleanLogs],
  );

  if (feedLoading || loading || !feedData || !data) return <Spinner />;
  if (feedError || error) return <>Error loading past visit data</>;

  return (
    <div>
      <Headline size="l" className="text-greyscale-0 dark:text-white pl-4 flex">
        {<Leaves className=" mr-2" />}
        {headlineText}
      </Headline>
      <ActivitiesFeed
        dateSettings={viewer?.tenantSettings}
        data={filteredFeedData ?? []}
        source="CareRecipient"
        loading={feedLoading}
        error={feedError}
        cardClick={handleCardClick}
        nameOrderSettings={viewer?.tenantSettings}
        shouldShowGoToTop={false}
      />
    </div>
  );
};
